
import Vue from "vue";
import Breadcrump from "@/components/common/Breadcrump.vue";
import request from "@/utils/request";
import moment from "moment-jalaali";

interface GameSmsItem {
  id: number;
  gameWon: boolean;
  title: string;
  message: string;
  createdAt: string;
  devicesCount?: number;
}

interface ApiDeviceItem {
  deviceId: number;
  deviceName: string;
  complexId: number;
  complexName: string;
  isAssigned: boolean;
  type?: string | null;
}

interface DeviceItem {
  deviceId: number;
  deviceName: string;
  type?: string | null;
}

interface ComplexItem {
  complexId: number;
  complexName: string;
  devices: DeviceItem[];
}

export default Vue.extend({
  components: { Breadcrump },

  data() {
    return {
      loading: false,
      smsList: [] as GameSmsItem[],
      total: 0,

      // Table options for server-side pagination
      options: {
        page: 1,
        itemsPerPage: 10,
        mustSort: true,
        sortBy: ["createdAt"],
        sortDesc: [true]
      },

      // Filters
      filter: {
        title: "",
        message: "",
        gameWon: null as boolean | null,
        fromDate: null as string | null,
        toDate: null as string | null
      },

      // Date pickers
      fromDate: null as string | null,
      toDate: null as string | null,

      // Assign dialog
      assignDialog: false,
      assignLoading: false,
      selectedSmsId: null as number | null,
      currentSmsMessage: "",
      selectedTags: [] as string[],
      allDevices: [] as ComplexItem[],
      selectedDevices: [] as number[],
      deviceSearch: "",

      // Delete dialog
      deleteDialog: false,
      deleteLoading: false,
      deleteItemId: null as number | null,
      deleteItemTitle: "",

      gameTypeItems: [
        { text: "همه", value: null },
        { text: "پیامک برد", value: true },
        { text: "پیامک باخت", value: false }
      ],

      crumbs: [
        { text: "خانه", href: "/", disabled: false },
        { text: "پیامک‌های بازی", href: "", disabled: true }
      ],

      headers: [
        { text: "عنوان پیامک", value: "title", sortable: true },
        { text: "نوع پیامک", value: "gameWon", sortable: true },
        { text: "متن پیامک", value: "message", sortable: false },
        { text: "تعداد دستگاه‌ها", value: "devicesCount", sortable: true },
        { text: "تاریخ ایجاد", value: "createdAt", sortable: true },
        { text: "عملیات", value: "actions", sortable: false }
      ]
    };
  },

  computed: {
    allTags(): string[] {
      const tags = new Set<string>();
      this.allDevices.forEach(c => {
        c.devices.forEach(d => {
          if (d.type) tags.add(d.type);
        });
      });
      return Array.from(tags);
    },
    filteredComplexes(): ComplexItem[] {
      const t = this.deviceSearch.toLowerCase();
      let filtered = this.allDevices
        .map(c => ({
          ...c,
          devices: c.devices.filter(
            d =>
              d.deviceName.toLowerCase().includes(t) ||
              c.complexName.toLowerCase().includes(t)
          )
        }))
        .filter(c => c.devices.length);
      if (this.selectedTags.length > 0)
        filtered = filtered
          .map(c => ({
            ...c,
            devices: c.devices.filter(d =>
              this.selectedTags.includes(d.type ?? "")
            )
          }))
          .filter(c => c.devices.length);
      return filtered;
    }
  },

  watch: {
    options: {
      deep: true,
      handler() {
        this.fetchData();
      }
    }
  },

  methods: {
    truncateText(text: string, length: number): string {
      if (!text) return "";
      return text.length > length ? text.slice(0, length) + "..." : text;
    },

    persianDate(d: string) {
      if (!d) return "-";
      return moment(d).format("jYYYY/jMM/jDD HH:mm");
    },

    async fetchData() {
      this.loading = true;
      try {
        const params = this.buildRequestParams();
        const res = await request.get("/GameSms/list", { params });
        this.smsList = res.data.result.result || res.data.result || [];
        this.total = res.data.result.totalItems || res.data.result.length || 0;
      } catch (error) {
        console.error("Error fetching SMS data:", error);
      } finally {
        this.loading = false;
      }
    },

    buildRequestParams() {
      const params: any = {
        page: this.options.page,
        itemsPerPage: this.options.itemsPerPage,
        mustSort: this.options.mustSort,
        sortBy: this.options.sortBy?.[0] || "createdAt",
        sortDesc: this.options.sortDesc?.[0] === true
      };

      if (this.filter.title) params.title = this.filter.title;
      if (this.filter.message) params.message = this.filter.message;
      if (this.filter.gameWon !== null) params.gameWon = this.filter.gameWon;

      if (this.fromDate) {
        params.fromDate = moment(this.fromDate, "jYYYY/jMM/jDD").format(
          "YYYY-MM-DD"
        );
      }
      if (this.toDate) {
        params.toDate = moment(this.toDate, "jYYYY/jMM/jDD").format(
          "YYYY-MM-DD"
        );
      }

      return params;
    },

    applyFilter() {
      this.options.page = 1;
      this.fetchData();
    },

    resetFilters() {
      this.filter = {
        title: "",
        message: "",
        gameWon: null,
        fromDate: null,
        toDate: null
      };
      this.fromDate = null;
      this.toDate = null;
      this.options.page = 1;
      this.fetchData();
    },

    editItem(item: GameSmsItem) {
      this.$router.push({
        name: "GameSmsFormEdit",
        params: { id: String(item.id) }
      });
    },

    deleteItem(item: GameSmsItem) {
      this.deleteItemId = item.id;
      this.deleteItemTitle = item.title;
      this.deleteDialog = true;
    },

    async confirmDelete() {
      if (!this.deleteItemId) return;
      this.deleteLoading = true;
      try {
        await request.delete(`/GameSms/${this.deleteItemId}`);
        await this.fetchData();
      } finally {
        this.deleteLoading = false;
        this.deleteDialog = false;
        this.deleteItemId = null;
      }
    },

    async openAssignModal(smsId: number) {
      this.selectedSmsId = smsId;
      const sms = this.smsList.find(s => s.id === smsId);
      this.currentSmsMessage = sms?.message || "";
      this.assignDialog = true;
      await this.loadDevicesForSms(smsId);
    },

    closeAssignModal() {
      this.assignDialog = false;
      this.selectedDevices = [];
      this.allDevices = [];
      this.selectedTags = [];
      this.deviceSearch = "";
    },

    async loadDevicesForSms(smsId: number) {
      const res = await request.get(`/GameSms/${smsId}/get-devices`);
      const list: ApiDeviceItem[] = res.data.result;

      const map = new Map<number, ComplexItem>();
      const assigned: number[] = [];

      list.forEach(i => {
        if (!map.has(i.complexId)) {
          map.set(i.complexId, {
            complexId: i.complexId,
            complexName: i.complexName || "نامشخص",
            devices: []
          });
        }
        const complex = map.get(i.complexId);
        if (!complex) return;

        complex.devices.push({
          deviceId: i.deviceId,
          deviceName: i.deviceName,
          type: i.type
        });

        if (i.isAssigned) assigned.push(i.deviceId);
      });

      this.allDevices = Array.from(map.values());
      this.selectedDevices = assigned;
    },

    getDeviceSimpleName(id: number) {
      for (const c of this.allDevices) {
        const d = c.devices.find(x => x.deviceId === id);
        if (d) return d.deviceName;
      }
      return "نامشخص";
    },

    getDeviceComplexName(id: number) {
      for (const c of this.allDevices) {
        const d = c.devices.find(x => x.deviceId === id);
        if (d) return c.complexName;
      }
      return "";
    },

    getDeviceType(id: number) {
      for (const c of this.allDevices) {
        const d = c.devices.find(x => x.deviceId === id);
        if (d) return d.type;
      }
      return null;
    },

    removeDevice(id: number) {
      this.selectedDevices = this.selectedDevices.filter(d => d !== id);
    },

    selectAllFilteredDevices() {
      const filteredIds = this.filteredComplexes.flatMap(c =>
        c.devices.map(d => d.deviceId)
      );
      this.selectedDevices = Array.from(
        new Set([...this.selectedDevices, ...filteredIds])
      );
    },

    async submitAssignment() {
      if (!this.selectedSmsId) return;
      this.assignLoading = true;

      try {
        await request.post("/GameSms/set-devices", {
          gameSmsId: this.selectedSmsId,
          deviceIds: this.selectedDevices
        });
        this.closeAssignModal();
        await this.fetchData();
      } finally {
        this.assignLoading = false;
      }
    }
  },

  created() {
    this.fetchData();
  }
});
